import React, { useEffect } from 'react';
import { Store as ReduxStore } from 'redux';
import fetchIntercept from 'fetch-intercept';
import TwoFactorAuthentication from '@users/two-factor-authentication/TwoFactorAuthentication';
import { initAutoDownload } from './initAutoDownload';
import SentryUserDataWrapper from './SentryUserDataWrapper';
import { GoogleAnalyticsProvider } from '@framework/browser/analytics/google';
import GlobalErrorContext from '@framework/error-handling/GlobalError/GlobalErrorContext';

import SessionContext from '@users/session/SessionContext';
import { InitAlerts } from '@dealroadshow/uikit-alert';
import registerMessagesExecCallback from '@framework/message/registerMessagesExecCallback';
import DIContext from '@framework/di/DIContext';
import Container from '@framework/di/Container';
import { TYPES } from '@framework/config/DI/types';
import { checkSession } from '@users/session/interceptors/fetch/checkSession';
import Store from '@framework/di/Providers/Redux/Store';
import { History } from '@framework/di/Providers/History';
import { InitSessionState } from '@users/session/serverSide/InitSessionState';
import { ISession } from '@users/session/vo/Session';
import { IUser } from '@users/user/vo/User';
import ClientCookie from '@framework/router/Next/cookie/ClientCookie';
import ClientReqData from '@framework/router/Next/headers/ClientReqData';

interface IProps {
  container: Container,
  store?: ReduxStore,
  session: {
    initialized: boolean,
    currentSession: ISession,
    currentUser: IUser,
    currentHost: string,
    fingerprint: string,
    userAgent: string,
  },
  children: React.ReactNode,
  history?: React.ComponentProps<typeof GlobalErrorContext>['history'],
  cookies: { [key: string]: any },
  renderAlerts?: React.ReactNode,
}

function Bootstrap({
  session, children, container, store, cookies, history, renderAlerts = <InitAlerts />,
}: IProps) {
  container.bindContextValue(ClientCookie, new ClientCookie(cookies), true);
  container.bindContextValue(ClientReqData, new ClientReqData({
    userAgent: session?.userAgent,
    fingerprint: session?.fingerprint,
    currentHost: session?.currentHost,
  }), true);
  container.bindContextValue(InitSessionState, session, true);

  const nonce = container.get<string>(TYPES.Nonce) || undefined;

  useEffect(() => {
    if (store) {
      container.bindValue(Store, store);
    }
    container.bindValue(History, history);

    // @ts-ignore
    fetchIntercept.register({ response: checkSession(container) });
    initAutoDownload(history);
    registerMessagesExecCallback(container);
  }, []);

  return (
    <DIContext container={ container }>
      { renderAlerts }
      <SessionContext>
        <SentryUserDataWrapper>
          <GoogleAnalyticsProvider nonce={ nonce } />
          <GlobalErrorContext history={ history }>
            <TwoFactorAuthentication>
              { children }
            </TwoFactorAuthentication>
          </GlobalErrorContext>
        </SentryUserDataWrapper>
      </SessionContext>
    </DIContext>
  );
}

export default Bootstrap;
